import { ChangeEventHandler, useRef, useState } from 'react'

import { useLightMode } from '@/hooks/useLightMode'

import {
  FormControl as ChakraFormControl,
  FormControlOptions,
  FormErrorMessage,
  FormLabel,
} from '@chakra-ui/react'

import { DarkProvider, FieldLabel } from '@hivewatch/dark'
import type { ColorScheme } from '@hivewatch/dark'

import { ButtonIProps } from '../Button/Button'
import { FileAttachButton } from './FileAttachButton'
import { FileUploadInput } from './FileUploadInput'

export interface FormInputControlIProps extends FormControlOptions {
  /**
   * Rebrand: keys the label, border and file row to the theme. Omit to keep
   * the legacy appearance untouched — every existing caller does.
   */
  colorScheme?: ColorScheme
  /** Optional rebrand text color for the file attachment action. */
  attachButtonTextColor?: string
  id: string
  label: string
  accept: string
  inputRef: (ref: HTMLInputElement) => void
  onFileRemoved?: () => void
  isInvalid?: boolean
  isDisabled?: boolean
  errorMessage?: string
  width?: string
  placeholder?: string
  [key: string]: unknown
  variant?: ButtonIProps['variant']
}

export const FormFileUploadControl = ({
  colorScheme,
  attachButtonTextColor,
  id,
  label,
  accept,
  isInvalid = false,
  isDisabled = false,
  errorMessage,
  width = '100%',
  placeholder = 'Upload file',
  inputRef,
  onFileRemoved,
  variant,
  ...rest
}: FormInputControlIProps) => {
  const mode = useLightMode()
  const [file, setFile] = useState<string | null>(null)
  const customRef = useRef<HTMLInputElement | null>(null)
  const hasFile = file !== null

  const onFileChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    // This will cause rerender when new file is uploaded
    setFile(e.target.files?.[0]?.name ?? null)
  }

  const onFileRemove = () => {
    if (customRef?.current) {
      // Reset attached file
      customRef.current.files = null
      customRef.current.value = ''
      customRef.current.dispatchEvent(new Event('change', { bubbles: true }))
      setFile(null)
      onFileRemoved?.()
    }
  }

  const onFileAttach = (e) => {
    // Clicks hidden input
    customRef?.current.click?.()
    e.target.blur()
  }

  return (
    <ChakraFormControl isInvalid={isInvalid} width={width}>
      {colorScheme ? (
        <DarkProvider colorScheme={colorScheme}>
          <FieldLabel colorScheme={colorScheme} htmlFor={id}>
            {label}
          </FieldLabel>
        </DarkProvider>
      ) : (
        <FormLabel
          color={`text.primary.${mode}`}
          fontSize='16px'
          htmlFor={id}
          opacity={0.5}
        >
          {label}
        </FormLabel>
      )}
      <input
        accept={accept}
        data-testid='FormFileUploadControl:input'
        disabled={isDisabled}
        hidden
        id={id}
        multiple={false}
        name={id}
        onChange={onFileChange}
        ref={(el) => {
          inputRef(el)
          customRef.current = el
        }}
        type='file'
        {...rest}
      />
      {hasFile ? (
        <FileUploadInput
          colorScheme={colorScheme}
          file={customRef?.current?.files?.[0]}
          isDisabled={isDisabled}
          isInvalid={isInvalid}
          onFileRemove={onFileRemove}
        />
      ) : (
        <FileAttachButton
          attachButtonTextColor={attachButtonTextColor}
          colorScheme={colorScheme}
          isDisabled={isDisabled}
          onFileAttach={onFileAttach}
          placeholder={placeholder}
          variant={variant}
        />
      )}
      <FormErrorMessage>{errorMessage}</FormErrorMessage>
    </ChakraFormControl>
  )
}
